import type { DeskDetail, DeskHeartbeat, ReportInterval, UpdateDeskRequest, WorkItemType } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CheckSquare, ClipboardList, Crown, HeartPulse, Pencil, Plus, Sparkles, Tags, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { GoalBadges, ReportStateBadge } from '@/components/report/report-parts';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

type HeartbeatUnit = DeskHeartbeat['every'];

const HEARTBEAT_UNITS: { value: HeartbeatUnit | 'off'; label: string }[] = [
  { value: 'off', label: t('Off – only events wake the Lead') },
  { value: 'minutes', label: t('Every … minutes') },
  { value: 'hours', label: t('Every … hours') },
  { value: 'daily', label: t('Daily at …') },
  { value: 'weekly', label: t('Mondays at …') },
  { value: 'monthly', label: t('On the 1st at …') },
];

/** A unit chosen anew starts at its usual value; the time of day carries over between daily, weekly and monthly. */
function heartbeatFor(unit: HeartbeatUnit, current: DeskHeartbeat | null): DeskHeartbeat {
  if (unit === 'minutes') return { every: unit, n: 10 };
  if (unit === 'hours') return { every: unit, n: 1 };
  return { every: unit, at: current && 'at' in current ? current.at : '08:00' };
}

/**
 * The Lead coordinates the desk (docs/features/self-organizing-desks.md): its standing item where triggers, the
 * heartbeat and "Run now" wake it, the heartbeat as a safety net, the desk's own kinds of work and the desk's own skill
 * versions. Its playbooks have their own section (desk-playbooks.tsx).
 */
export function DeskLeadSection({ desk }: { desk: DeskDetail }) {
  return (
    <Section
      icon={<Crown />}
      title={t('{lead} coordinates', { lead: desk.leadAgent?.name ?? t('The Lead') })}
      description={t('Only the Lead starts runs. Between them it sleeps: events wake it (new work, a run ended, a decision, a due date), the heartbeat makes it check for work that hangs.')}
    >
      <div className="flex flex-col gap-5 text-sm">
        <Heartbeat desk={desk} />
        {desk.watchWorkItemId && (
          <p className="text-xs text-muted-foreground">
            {(() => {
              const item = (
                <Link to="/w/$workspaceId/work-items/$workItemId" params={{ workspaceId: desk.workspaceId, workItemId: desk.watchWorkItemId }} className="underline hover:text-foreground">
                  {t('“Check your desk”')}
                </Link>
              );
              return !desk.pendingEvents
                ? tr('The Lead checks the desk on its standing item {item}.', { item })
                : desk.pendingEvents === 1
                  ? tr('The Lead checks the desk on its standing item {item} · 1 event waiting for it.', { item })
                  : tr('The Lead checks the desk on its standing item {item} · {n} events waiting for it.', { item, n: desk.pendingEvents });
            })()}
          </p>
        )}
        <Reports desk={desk} />
        <WorkItemTypes desk={desk} />
        <DeskSkills desk={desk} />
      </div>
    </Section>
  );
}

/**
 * The heartbeat, apart from the triggers: triggers make the Lead look at its sources, the heartbeat at the desk's state.
 * It wakes the Lead only on something that hangs and that it hasn't woken it for yet.
 */
function Heartbeat({ desk }: { desk: DeskDetail }) {
  const hb = desk.heartbeat;
  const [draft, setDraft] = useState<string | null>(null);
  const update = useApiMutation((heartbeat: DeskHeartbeat | null) => api.desks.update(desk.id, { heartbeat }), {
    invalidate: [qk.desk(desk.id)],
    success: (_d, heartbeat) => (heartbeat ? t('Heartbeat set — the Lead checks the desk regularly') : t('Heartbeat off — only events wake the Lead')),
    onSuccess: () => setDraft(null),
  });
  const value = hb ? ('n' in hb ? String(hb.n) : hb.at) : '';
  const commit = () => {
    if (!hb || draft === null || draft === value) return setDraft(null);
    if ('n' in hb) {
      const n = Math.round(Number(draft));
      if (!Number.isFinite(n) || n < 1 || n > (hb.every === 'hours' ? 168 : 1440)) return setDraft(null);
      update.mutate({ every: hb.every, n });
    } else if (/^([01]\d|2[0-3]):[0-5]\d$/.test(draft)) update.mutate({ every: hb.every, at: draft });
    else setDraft(null);
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <HeartPulse className="size-4 text-rose-500" />
          <span className="font-medium">{t('Heartbeat')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={hb?.every ?? 'off'}
            onValueChange={(v) => update.mutate(v === 'off' ? null : heartbeatFor(v as HeartbeatUnit, hb))}
            disabled={update.isPending}
          >
            <SelectTrigger size="sm" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HEARTBEAT_UNITS.map((u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hb && (
            <Input
              type={'n' in hb ? 'number' : 'time'}
              min={'n' in hb ? 1 : undefined}
              max={'n' in hb ? (hb.every === 'hours' ? 168 : 1440) : undefined}
              value={draft ?? value}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => e.key === 'Enter' && commit()}
              disabled={update.isPending}
              aria-label={'n' in hb ? (hb.every === 'hours' ? t('Hours') : t('Minutes')) : t('Time')}
              className="h-8 w-24"
            />
          )}
        </div>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {t('Deskworks checks the desk for runs without a sign of life, overdue work, decisions nobody made and work that waits for the Lead, and wakes the Lead only when it finds something new. Looking at the sources for new work is up to the triggers.')}
        {hb && desk.heartbeatNextAt && (
          <>
            {' '}
            {tr('Next check {time}.', { time: <RelativeTime value={desk.heartbeatNextAt} /> })}
          </>
        )}
      </p>
    </div>
  );
}

const REPORT_INTERVALS: { value: string; label: string; interval: ReportInterval | null }[] = [
  { value: 'off', label: t('Off – only “Report now”'), interval: null },
  { value: 'daily', label: t('Daily, 07:00'), interval: 'daily' },
  { value: 'weekly', label: t('Weekly, Monday 07:00'), interval: 'weekly' },
  { value: 'monthly', label: t('Monthly, on the 1st'), interval: 'monthly' },
];

/** How often the Lead reports, and whether it improves the desk afterwards (docs/features/desk-reports.md). */
function Reports({ desk }: { desk: DeskDetail }) {
  const navigate = useNavigate();
  const lead = desk.leadAgent?.name ?? t('The Lead');
  const invalidate = [qk.desk(desk.id), qk.reportsAll(desk.workspaceId)];
  const update = useApiMutation((body: UpdateDeskRequest) => api.desks.update(desk.id, body), { invalidate, success: t('Reports updated') });
  const now = useApiMutation(() => api.desks.report(desk.id), {
    invalidate,
    success: t('{lead} writes the report', { lead }),
    onSuccess: (r) => void navigate({ to: '/w/$workspaceId/work-items/$workItemId', params: { workspaceId: desk.workspaceId, workItemId: r.workItemId } }),
  });
  const latest = useQuery({ queryKey: qk.reports(desk.workspaceId, desk.id), queryFn: () => api.reports.list(desk.workspaceId, { deskId: desk.id }) });
  const last = latest.data?.[0];
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 font-medium">
          <ClipboardList className="size-4 text-muted-foreground" /> {t('Reports')}
        </span>
        <div className="flex items-center gap-2">
          <Select
            value={desk.reportInterval ?? 'off'}
            onValueChange={(v) => update.mutate({ reportInterval: REPORT_INTERVALS.find((r) => r.value === v)?.interval ?? null })}
            disabled={update.isPending}
          >
            <SelectTrigger size="sm" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_INTERVALS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => now.mutate()} loading={now.isPending} disabled={!desk.leadAgent || desk.status !== 'active'}>
            {t('Report now')}
          </Button>
        </div>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        {t('{lead} reports where the desk stands on its goals. You answer in the report’s thread; when everything is said, “Done – improve” lets {lead} improve its playbooks and skills.', { lead })}
        {desk.reportInterval && desk.reportNextAt && (
          <>
            {' '}
            {tr('Next report {time}.', { time: <RelativeTime value={desk.reportNextAt} /> })}
          </>
        )}
      </p>
      <div className="flex flex-col gap-1.5 text-xs">
        <label className="flex items-center gap-2">
          <Switch checked={desk.reportImprove} onCheckedChange={(v) => update.mutate({ reportImprove: v })} disabled={update.isPending} />
          {t('{lead} improves the desk after each report', { lead })}
        </label>
        <label className="flex items-center gap-2">
          <Switch checked={desk.reportWhenQuiet} onCheckedChange={(v) => update.mutate({ reportWhenQuiet: v })} disabled={update.isPending} />
          {t('Report also when nothing happened — silence can mean something is broken')}
        </label>
      </div>
      {last && (
        <Link
          to="/w/$workspaceId/work-items/$workItemId"
          params={{ workspaceId: desk.workspaceId, workItemId: last.workItemId }}
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 hover:bg-muted/40"
        >
          <span className="min-w-0 flex-1 truncate font-medium">{last.title}</span>
          <GoalBadges report={last} />
          <ReportStateBadge report={last} />
        </Link>
      )}
      {latest.data && latest.data.length > 1 && (
        <Link to="/w/$workspaceId/reports" params={{ workspaceId: desk.workspaceId }} search={{ deskId: desk.id }} className="mt-1 inline-block text-xs text-muted-foreground underline hover:text-foreground">
          {t('All {n} reports', { n: latest.data.length })}
        </Link>
      )}
    </div>
  );
}

type TypeDraft = { name: string; statuses: string; fields: string; done: string; playbook: string; planApproval: boolean };

const NO_PLAYBOOK = '__none__';

function WorkItemTypes({ desk }: { desk: DeskDetail }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<TypeDraft>({ name: '', statuses: '', fields: '', done: '', playbook: '', planApproval: false });
  const invalidate = [qk.desk(desk.id)];
  const points = (text: string) => text.split('\n').map((l) => l.replace(/^\s*(?:[-*]|\d+[.)])\s*/, '').trim()).filter(Boolean);
  const reset = () => {
    setAdding(false);
    setEditing(null);
    setDraft({ name: '', statuses: '', fields: '', done: '', playbook: '', planApproval: false });
  };
  const create = useApiMutation(
    () => {
      const properties = Object.fromEntries(
        draft.fields
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean)
          .map((f) => {
            const [field, type] = f.split(':').map((x) => x.trim());
            return [field, { type: type || 'string' }];
          }),
      );
      return api.desks.createWorkItemType(desk.id, {
        name: draft.name,
        statuses: draft.statuses.split(',').map((s) => s.trim()).filter(Boolean),
        fieldsSchema: { type: 'object', properties },
        definitionOfDone: points(draft.done),
        playbookName: draft.playbook || null,
        planApproval: draft.planApproval,
      });
    },
    { invalidate, success: t('Kind of work added'), onSuccess: reset },
  );
  const update = useApiMutation(
    (wt: WorkItemType) => api.desks.updateWorkItemType(wt.id, { definitionOfDone: points(draft.done), playbookName: draft.playbook || null, planApproval: draft.planApproval }),
    { invalidate, success: t('Kind of work saved — new items get the new definition of done'), onSuccess: reset },
  );
  const remove = useApiMutation((wt: WorkItemType) => api.desks.deleteWorkItemType(wt.id), { invalidate, success: t('Kind of work removed') });
  const startEdit = (wt: WorkItemType) => {
    setAdding(false);
    setEditing(wt.id);
    setDraft({ name: wt.name, statuses: '', fields: '', done: wt.definitionOfDone.join('\n'), playbook: wt.playbookName ?? '', planApproval: wt.planApproval });
  };

  const doneAndPlaybook = (
    <>
      {/* A person approves the plan before the runs start (docs/features/work-items.md#the-plan). */}
      <label className="flex cursor-pointer items-center justify-between gap-3 text-sm">
        <span>
          {t('Approve the plan before the work starts')}
          <span className="block text-xs text-muted-foreground">{t('The Lead shows you its plan first, e.g. for features or milestones.')}</span>
        </span>
        <Switch checked={draft.planApproval} onCheckedChange={(v) => setDraft({ ...draft, planApproval: v })} />
      </label>
      <Textarea
        value={draft.done}
        onChange={(e) => setDraft({ ...draft, done: e.target.value })}
        rows={4}
        placeholder={t('Definition of done, one point per line, e.g.\nThe reviewer passed the checklist\nThe article is live\nThe URL is submitted in Search Console')}
      />
      <Select value={draft.playbook || NO_PLAYBOOK} onValueChange={(v) => setDraft({ ...draft, playbook: v === NO_PLAYBOOK ? '' : v })}>
        <SelectTrigger size="sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_PLAYBOOK}>{t('No playbook')}</SelectItem>
          {desk.playbooks.map((p) => (
            <SelectItem key={p.id} value={p.name}>
              {t('Playbook: {name}', { name: p.name })}
            </SelectItem>
          ))}
          {draft.playbook && !desk.playbooks.some((p) => p.name.toLowerCase() === draft.playbook.toLowerCase()) && (
            <SelectItem value={draft.playbook}>{t('Playbook: {name} (not written yet)', { name: draft.playbook })}</SelectItem>
          )}
        </SelectContent>
      </Select>
    </>
  );

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-2 font-medium">
          <Tags className="size-4 text-muted-foreground" /> {t('Kinds of work')}
        </span>
        {!adding && !editing && (
          <Button variant="ghost" size="xs" onClick={() => setAdding(true)}>
            <Plus /> {t('Add')}
          </Button>
        )}
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        {t('Work always lives in Work Items. A kind of work (e.g. Milestone, Feature, Bug) gives them fields, the desk’s own status values, a definition of done every item must meet before the Lead closes it, and the playbook the team follows. Tables are for data and knowledge.')}
      </p>
      {adding && (
        <div className="mb-3 grid gap-2 rounded-lg border p-3">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={t('Name, e.g. Bug')} autoFocus />
          <Input value={draft.statuses} onChange={(e) => setDraft({ ...draft, statuses: e.target.value })} placeholder={t('Statuses in order, e.g. triage, fixing, in review, done')} />
          <Input value={draft.fields} onChange={(e) => setDraft({ ...draft, fields: e.target.value })} placeholder={t('Fields, e.g. severity, estimate:number, found_on:date')} />
          {doneAndPlaybook}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={reset}>
              {t('Cancel')}
            </Button>
            <Button size="sm" disabled={!draft.name.trim()} loading={create.isPending} onClick={() => create.mutate()}>
              {t('Add')}
            </Button>
          </div>
        </div>
      )}
      {desk.workItemTypes.length ? (
        <ul className="flex flex-col gap-1.5">
          {desk.workItemTypes.map((wt) =>
            editing === wt.id ? (
              <li key={wt.id} className="grid gap-2 rounded-md border p-3">
                <div className="font-medium">{wt.name}</div>
                {doneAndPlaybook}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={reset}>
                    {t('Cancel')}
                  </Button>
                  <Button size="sm" loading={update.isPending} onClick={() => update.mutate(wt)}>
                    {t('Save')}
                  </Button>
                </div>
              </li>
            ) : (
              <li key={wt.id} className="flex items-start justify-between gap-3 rounded-md border px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium">{wt.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {wt.statuses.length ? wt.statuses.join(' → ') : t('no own statuses')}
                    {Object.keys((wt.fieldsSchema as any)?.properties ?? {}).length
                      ? ` · ${t('fields: {list}', { list: Object.keys((wt.fieldsSchema as any).properties).join(', ') })}`
                      : ''}
                    {wt.playbookName ? ` · ${t('playbook: {name}', { name: wt.playbookName })}` : ''}
                    {wt.planApproval ? ` · ${t('plan approved first')}` : ''}
                    {wt.createdByType === 'agent' ? ` · ${t('defined by the Lead')}` : ''}
                  </div>
                  {wt.definitionOfDone.length > 0 ? (
                    <ul className="mt-1.5 flex flex-col gap-0.5 text-xs">
                      {wt.definitionOfDone.map((d) => (
                        <li key={d} className="flex gap-1.5">
                          <CheckSquare className="mt-px size-3.5 shrink-0 text-muted-foreground" /> {d}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t('No definition of done yet — the Lead decides itself when an item is done.')}</div>
                  )}
                </div>
                <div className="flex shrink-0">
                  <Button variant="ghost" size="icon" aria-label={t('Edit {name}', { name: wt.name })} title={t('Definition of done and playbook')} onClick={() => startEdit(wt)}>
                    <Pencil />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={t('Remove {name}', { name: wt.name })} onClick={() => remove.mutate(wt)}>
                    <Trash2 />
                  </Button>
                </div>
              </li>
            ),
          )}
        </ul>
      ) : (
        !adding && <p className="text-xs text-muted-foreground">{t('None yet — the Lead can define them as the work shows what it needs.')}</p>
      )}
    </div>
  );
}

function DeskSkills({ desk }: { desk: DeskDetail }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!desk.deskSkills.length) return null;
  const promotion = { proposed: t('proposed for the library — waits for your approval'), promoted: t('now in the library'), rejected: t('not taken into the library') };
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 font-medium">
        <Sparkles className="size-4 text-muted-foreground" /> {t('The desk’s own skill versions')}
      </div>
      <p className="mb-2 text-xs text-muted-foreground">{t('Skills the Lead improved for this desk. Every agent of the desk uses them; the library only changes after you approve.')}</p>
      <ul className="flex flex-col gap-1.5">
        {desk.deskSkills.map((s) => (
          <li key={s.id} className="rounded-md border">
            <button className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left" onClick={() => setOpen(open === s.id ? null : s.id)}>
              <span className="font-medium">{s.name}</span>
              <span className="text-xs text-muted-foreground">
                {t('version {n}', { n: s.version })}
                {s.promotion ? ` · ${promotion[s.promotion]}` : ''}
                {s.status === 'retired' && !s.promotion ? ` · ${t('retired')}` : ''}
              </span>
            </button>
            {open === s.id && (
              <div className="border-t px-3 py-2">
                <p className="mb-2 text-xs text-muted-foreground">{s.description}</p>
                <Textarea readOnly value={s.body} rows={8} className="font-mono text-[12px]" />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
